// Builds a clean, background-removed, per-foot crop with the scan's real annotations (lines +
// valik) drawn on top — shared by the saved report's "Обычный скан" images (App.jsx
// buildReportPayload) and the "Сканирования" preview modal (DocumentsPage.jsx DocModal). Reuses
// removeDarkBackground (imageProcessing.js) and drawAnnotations (annotationRenderer.js) rather
// than reimplementing either.
import { removeDarkBackground } from "./imageProcessing.js";
import { drawAnnotations } from "./annotationRenderer.js";

async function cropToCanvas(base64, region) {
  if (!base64 || !region) return null;
  const x = Math.round(Math.min(region.x1, region.x2));
  const y = Math.round(Math.min(region.y1, region.y2));
  const w = Math.round(Math.abs(region.x2 - region.x1));
  const h = Math.round(Math.abs(region.y2 - region.y1));
  if (w < 2 || h < 2) return null;
  try {
    const src = base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`;
    const resp = await fetch(src);
    const blob = await resp.blob();
    const bitmap = await createImageBitmap(blob, x, y, w, h);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    bitmap.close();
    return { canvas, offsetX: x, offsetY: y };
  } catch (e) {
    console.error("cropToCanvas failed:", e);
    return null;
  }
}

// rawBase64: the unprocessed scan (data URI or bare base64). annotations: the scan's full
// annotations array (region boxes decide the crop; lines/valik get drawn using the rest).
// wbSettings: forwarded to removeDarkBackground — omit to use its built-in defaults (the case
// when there's no live scanner-panel settings object around, e.g. viewing an already-saved doc).
// Returns { left, right } data URLs (JPEG), either null if that foot has no region annotation.
export async function buildAnnotatedFootCrops(rawBase64, { annotations = [], padSizes = [], padSizesPerFoot = {}, dpi = 150, wbSettings = {} } = {}) {
  if (!rawBase64) return { left: null, right: null };
  const processed = await removeDarkBackground(rawBase64, wbSettings);
  const leftRegion = annotations.find(a => a.type === "region" && a.foot === "left");
  const rightRegion = annotations.find(a => a.type === "region" && a.foot === "right");

  const render = async (region) => {
    const cropped = await cropToCanvas(processed, region);
    if (!cropped) return null;
    const { canvas, offsetX, offsetY } = cropped;
    drawAnnotations(canvas.getContext("2d"), {
      annotations, padSizes, padSizesPerFoot, dpi, scale: 1, showLabels: true,
      offsetX, offsetY, drawRegionBoxes: false,
    });
    return canvas.toDataURL("image/jpeg", 0.9);
  };

  const [left, right] = await Promise.all([render(leftRegion), render(rightRegion)]);
  return { left, right };
}
